import { capitalize } from 'lodash';
import { BoxV2, Input, Selector, SelectorOptionPropsInterface } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { ContextualDataProviderCampaign } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import {
  CONTEXTUAL_THIRD_PARTY_DATA_PROVIDERS,
  ContextualThirdPartyDataProvider
} from 'sonata-static-storage/lib/schemas/user/contextualThirdPartyDataProvider.schema';

import { ProvidersEnum } from '../Steps/StepFour/CampaignAdditionalDetails/CampaignAdditionalDetails.tsx';
import { ContextualDataVariablesStyled } from './contextualDataVariables.styled.ts';

import type { CurrencyInfo } from 'sonata-static-storage/lib/samples/currencies.samples';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

type Props = {
  contextualDataProviders: ContextualDataProviderCampaign[];
  contextualThirdPartyProviderVariables: Record<string, { group: string; id: string; name: string }[]>;
  currencyInfo: CurrencyInfo;
  onContextualDataProviderChange: (provider: string, enabled: boolean, percentage: number, ids: string[]) => void;
  user: UserInterface;
  userRoles: UserRoleInterface[];
};

const CONTEXTUAL_DATA_PROVIDERS_WIDTH_VARIABLES_SELECTION: ContextualThirdPartyDataProvider[] = [
  ProvidersEnum.mastercard
];
const CONTEXTUAL_DATA_PROVIDERS_WIDTH_BILL_PACING: ContextualThirdPartyDataProvider[] = [
  ProvidersEnum.mastercard,
  ProvidersEnum.circana,
  ProvidersEnum.wardem
];

export function ContextualDataVariables({
  contextualDataProviders,
  contextualThirdPartyProviderVariables,
  currencyInfo,
  onContextualDataProviderChange,
  user,
  userRoles
}: Props) {
  const isProviderVisible = (provider: ContextualThirdPartyDataProvider) => {
    return (
      user?.contextualDataProviders?.some(providerConfig => {
        return providerConfig.provider === provider && providerConfig.enabled;
      }) &&
      (!CONTEXTUAL_DATA_PROVIDERS_WIDTH_VARIABLES_SELECTION.includes(provider) ||
        contextualThirdPartyProviderVariables[provider].length)
    );
  };

  const availableProviders = CONTEXTUAL_THIRD_PARTY_DATA_PROVIDERS.filter(isProviderVisible);
  if (!availableProviders.length || !checkUserResourcePermissions('contextualDataProviders', user, userRoles)) {
    return null;
  }
  return (
    <ContextualDataVariablesStyled className="contextualDataVariables">
      {CONTEXTUAL_THIRD_PARTY_DATA_PROVIDERS.filter(isProviderVisible).map(provider => {
        const userProviderConfiguration = user?.contextualDataProviders?.find(providerConfig => {
          return providerConfig.provider === provider;
        });

        const providerCampaignConfiguration = contextualDataProviders.find(providerConfig => {
          return providerConfig.provider === provider;
        });

        const variables: SelectorOptionPropsInterface[] =
          contextualThirdPartyProviderVariables[provider]?.map((item: { group: string; id: string; name: string }) => ({
            group: item.group,
            id: item.id,
            label: item.name
          })) || [];

        const includeBillPacingConfiguration =
          CONTEXTUAL_DATA_PROVIDERS_WIDTH_BILL_PACING.includes(provider) &&
          checkUserResourcePermissions('contextualDataProvidersPercentage', user, userRoles);
        const includeVariablesConfiguration = CONTEXTUAL_DATA_PROVIDERS_WIDTH_VARIABLES_SELECTION.includes(provider);
        const includeProviderConfiguration = includeVariablesConfiguration || includeBillPacingConfiguration;

        return (
          <div className={`contextualDataVariables__${provider}`} key={`contextualDataVariables-${provider}`}>
            <BoxV2 className={`${provider}VariablesBox`} id={`${provider}VariablesBox`}>
              <BoxV2.Header
                bgColor="boxBgColor02"
                className={`${provider}Variables__header`}
                expandablePosition="left"
                expandableType="switch"
                expanded={contextualDataProviders?.some(item => item.provider === provider)}
                isExpandable
                onExpandedChange={(_: string, checked: boolean) => {
                  onContextualDataProviderChange(provider, checked, userProviderConfiguration!.value, []);
                }}
                padding="12px"
                separatorColor="boxBorderColor01"
              >
                <BoxV2.Header.Title
                  title={`Enable ${capitalize(provider)} Variables for Payment Usage`}
                  titleFontStyles={`${provider}Variables__title fontSemiBold`}
                />
              </BoxV2.Header>
              {includeProviderConfiguration && (
                <BoxV2.Body className="mastercardVariables__container" padding="12px">
                  <div className="mastercardVariables__inputs">
                    {includeVariablesConfiguration && (
                      <div className="mastercardVariables__inputsSelector">
                        <Selector
                          id="mastercardVariables-selector"
                          isDisabled={!provider}
                          label="Select variables"
                          onChange={(val: any[]) =>
                            onContextualDataProviderChange(
                              ProvidersEnum.mastercard,
                              true,
                              providerCampaignConfiguration?.value || userProviderConfiguration!.value,
                              val.map(i => i.id)
                            )
                          }
                          options={variables}
                          required
                          type="multiple"
                          value={
                            providerCampaignConfiguration
                              ? variables.filter(i => providerCampaignConfiguration.segmentIds.includes(i.id as string))
                              : []
                          }
                          withGrouping
                        />
                      </div>
                    )}
                    {includeBillPacingConfiguration && (
                      <div className={`${provider}Variables__inputsBill`}>
                        <Input
                          currencyConfig={currencyInfo}
                          defaultValue={userProviderConfiguration!.value}
                          id={`${provider}Variables-input`}
                          inputAdditionalProps={{
                            max: 100,
                            min: 0
                          }}
                          label="Bill Pacing Setting"
                          name={`${provider}Variables-input`}
                          numberFormat="percentage"
                          numberOfDecimals={2}
                          onChange={val =>
                            onContextualDataProviderChange(
                              provider,
                              true,
                              parseFloat(val as string),
                              providerCampaignConfiguration?.segmentIds || []
                            )
                          }
                          required
                          type="text"
                        />
                      </div>
                    )}
                  </div>
                </BoxV2.Body>
              )}
            </BoxV2>
          </div>
        );
      })}
    </ContextualDataVariablesStyled>
  );
}
